import styled, { css, DefaultTheme, keyframes } from 'styled-components';

export type PathProgressTrackSize = 'home' | 'compact';

/**
 * Both sizes place session index `i` of `n` at ratio `i / n` along the track,
 * from the start-node centre to the checkpoint-node centre. Keep this formula
 * in one place so Home and EOS stay in sync.
 */
const trackPosition = (theme: DefaultTheme, size: PathProgressTrackSize, ratio: number): string => {
  const clamped = Math.max(0, Math.min(1, ratio));
  if (size === 'home') {
    return `calc(${theme.sz.s6} + (100% - ${theme.sz.s16}) * ${clamped})`;
  }
  return `calc(${theme.sz.s5} + (100% - ${theme.sz.s11}) * ${clamped})`;
};

const herePillBob = keyframes`
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.2rem);
  }
`;

/**
 * Home needs extra right/bottom padding so the checkpoint name pill (centered
 * under the end node, up to s40 wide) isn't clipped by the card's
 * `overflow: hidden`. Compact EOS has no pills, so it only needs a small inset.
 */
export const ProgressSection = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${$size === 'home' ? `0 ${theme.sz.s12} ${theme.sz.s7} ${theme.sz.s2}` : `0 ${theme.sz.s2}`};

    ${
      $size === 'home' &&
      css`
        @media (max-width: ${theme.sz.lg}) {
          padding: 0 ${theme.sz.s10} ${theme.sz.s6} ${theme.sz.s1};
        }
      `
    }
  `,
);

/** Track centerline lives in the middle of this row. */
export const TrackRow = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: ${$size === 'home' ? theme.sz.s16 : theme.sz.s12};
  `,
);

/**
 * Invert dark mode turns pale yellow into a brown that vanishes on the dark
 * glass card. Source a dark band so invert restores a light, readable road.
 */
const darkModeTrack = css(
  ({ theme }) => css`
    html[data-color-scheme='dark'] & {
      background-color: ${theme.co.secondary20};
    }
  `,
);

export const Track = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    position: absolute;
    left: ${$size === 'home' ? theme.sz.s6 : theme.sz.s5};
    right: ${$size === 'home' ? theme.sz.s10 : theme.sz.s6};
    top: 50%;
    transform: translateY(-50%);
    height: ${$size === 'home' ? theme.sz.s6 : theme.sz.s3};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary90};
    overflow: hidden;
    ${darkModeTrack};
  `,
);

export const TrackFill = styled.div<{ $ratio: number }>(
  ({ theme, $ratio }) => css`
    height: 100%;
    width: ${Math.max(0, Math.min(1, $ratio)) * 100}%;
    background-color: ${theme.co.primary20};
    border-radius: ${theme.bo.round};
    transition: width 0.3s ease;
  `,
);

export const DotsRow = styled.div(
  () => css`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
  `,
);

export const LessonDot = styled.span<{ $ratio: number; $size: PathProgressTrackSize }>(
  ({ theme, $ratio, $size }) => css`
    position: absolute;
    left: ${trackPosition(theme, $size, $ratio)};
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${theme.sz.s2};
    height: ${theme.sz.s2};
    border-radius: ${theme.bo.elliptical};
    background-color: ${theme.co.neutral99};
    border: ${theme.bo.thinWidth} solid ${theme.co.primary70};
  `,
);

export const StartNodeWrap = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: ${$size === 'home' ? theme.sz.s8 : theme.sz.s10};
    height: ${$size === 'home' ? theme.sz.s8 : theme.sz.s10};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);

export const StartNode = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${$size === 'home' ? theme.sz.s8 : theme.sz.s10};
    height: ${$size === 'home' ? theme.sz.s8 : theme.sz.s10};
    border-radius: ${theme.bo.elliptical};
    background-color: ${theme.co.primary20};

    svg {
      width: ${theme.sz.s4};
      height: ${theme.sz.s4};
      color: ${theme.co.neutral10};
    }
  `,
);

/**
 * Left-aligned rather than centered under the node: the node sits flush against
 * the card's left padding, so a centered pill would be clipped.
 */
export const StartPill = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: calc(100% + ${theme.sz.s1});
    left: 0;
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.neutral40};
    background-color: ${theme.co.neutral95};
    border-radius: ${theme.bo.round};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    white-space: nowrap;
    max-width: ${theme.sz.s40};
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;

    html[data-color-scheme='dark'] & {
      color: ${theme.co.neutral99};
      background-color: ${theme.co.secondary20};
    }
  `,
);

export const HereMarker = styled.div<{ $ratio: number; $size: PathProgressTrackSize }>(
  ({ theme, $ratio, $size }) => css`
    position: absolute;
    left: ${trackPosition(theme, $size, $ratio)};
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;

    ${
      $size === 'home'
        ? css`
            display: flex;
            flex-direction: column;
            align-items: center;
          `
        : css`
            width: ${theme.sz.s5};
            height: ${theme.sz.s5};
            border-radius: ${theme.bo.elliptical};
            background-color: ${theme.co.primary20};
            box-shadow: 0 0 0 ${theme.sz.s05} ${theme.co.neutral99};

            html[data-color-scheme='dark'] & {
              box-shadow: 0 0 0 ${theme.sz.s05} ${theme.co.secondary0};
            }
          `
    }
  `,
);

export const HerePill = styled.span(
  ({ theme }) => css`
    position: absolute;
    bottom: calc(100% + ${theme.sz.s2});
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.secondary20};
    background-color: ${theme.co.neutral99};
    border: ${theme.bo.mediumWidth} solid ${theme.co.primary20};
    border-radius: ${theme.bo.round};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    white-space: nowrap;
    line-height: 1;
    animation: ${herePillBob} 2.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }

    html[data-color-scheme='dark'] & {
      color: ${theme.co.neutral99};
      background-color: ${theme.co.neutral0};
    }
  `,
);

export const HereDot = styled.span(
  ({ theme }) => css`
    width: ${theme.sz.s4};
    height: ${theme.sz.s4};
    border-radius: ${theme.bo.elliptical};
    background-color: ${theme.co.primary20};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &::after {
      content: '';
      width: ${theme.sz.s1};
      height: ${theme.sz.s1};
      border-radius: ${theme.bo.elliptical};
      background-color: ${theme.co.neutral10};
    }

    html[data-color-scheme='dark'] &::after {
      background-color: ${theme.co.neutral99};
    }
  `,
);

export const CheckpointNodeWrap = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: ${$size === 'home' ? theme.sz.s20 : theme.sz.s12};
    height: ${$size === 'home' ? theme.sz.s20 : theme.sz.s12};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);

export const CheckpointNode = styled.div<{ $size: PathProgressTrackSize }>(
  ({ theme, $size }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${$size === 'home' ? theme.sz.s20 : theme.sz.s10};
    height: ${$size === 'home' ? theme.sz.s20 : theme.sz.s10};
    border-radius: ${theme.bo.elliptical};
    background-color: ${theme.co.neutral99};
    border: ${theme.bo.thickWidth} solid ${theme.co.primary20};
    box-shadow: 0 0 0 ${$size === 'home' ? theme.sz.s2 : theme.sz.s1} ${theme.co.primary90};

    svg {
      width: ${$size === 'home' ? theme.sz.s8 : theme.sz.s5};
      height: ${$size === 'home' ? theme.sz.s8 : theme.sz.s5};
      color: ${theme.co.neutral10};
    }

    html[data-color-scheme='dark'] & {
      background-color: ${theme.co.neutral0};
      box-shadow: 0 0 0 ${$size === 'home' ? theme.sz.s2 : theme.sz.s1} ${theme.co.secondary20};

      svg {
        color: ${theme.co.neutral99};
      }
    }
  `,
);

export const CheckpointPill = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: calc(100% + ${theme.sz.s1});
    left: 50%;
    transform: translateX(-50%);
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.neutral10};
    background-color: ${theme.co.primary20};
    border-radius: ${theme.bo.round};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    white-space: nowrap;
    max-width: ${theme.sz.s40};
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;

    html[data-color-scheme='dark'] & {
      color: ${theme.co.neutral99};
    }
  `,
);
